import classNames from "./CreateQRCodeView.module.css";
import CreateQRCodeContext, {CreateQRCodeStep} from "./CreateQRCodeContext";
import {ReactNode, useEffect, useState} from "react";
import QRCodeKind from "../../../api/qr-code/QRCodeKind";
import ChooseKind from "./choose-kind/ChooseKind";
import InputData from "./input-data/InputData";
import CreatePayload from "../../../api/qr-code/CreatePayload";
import {Button} from "@mantine/core";
import Overlay from "./_utils/overlay/Overlay";
import QRCodeApi from "../../../api/qr-code/QRCodeApi";
import QRCodeHelper from "../../../helpers/qr-code/QRCodeHelper";

type CreateQRCodeViewProps = {};

/**
 * Create QR code view.
 * @author Kacper Faber
 * @since 0.1.8
 * @param _props
 * @constructor
 */
function CreateQRCodeView(_props: CreateQRCodeViewProps) {
    /**
     * Selected QR code kind.
     * You must select what kinda QR you want to generate, to go to the next step.
     * Part of CreateQRCodeViewContext.
     * @state QRCodeKind | null
     * @author Kacper Faber
     * @since 0.1.8
     */
        // TODO: Delete it and use kind from payload
    const [selectedKind, setSelectedKind] = useState<QRCodeKind | null>(null);

    const [step, setStep] = useState<CreateQRCodeStep>('choose-kind');

    /**
     * Get default payload to be used in state.
     * @author Kacper Faber
     * @since 0.1.8
     */
    const getDefaultPayload = () => {
        return { name: '', key: QRCodeHelper.getRandomKey(), kind: 'website' } as CreatePayload;
    }

    // TODO: CreateQRCodeView.tsx
    //  Think about use undefined payload, if it's not set yet. And fill it later with real data.

    /**
     * Payload to be sent
     * @author Kacper Faber
     * @since 0.1.8
     */
    const [payload, setPayload] = useState<CreatePayload>(getDefaultPayload());

    /**
     * Name of QRCode to be created.
     * @author Kacper Faber
     * @since 0.1.8
     */
    const [name, setName] = useState<string>();

    /**
     * Check if given payload [selectedKind and name states] are valid.
     * You can show 'create' button or sth if payload is valid.
     * @author Kacper Faber
     * @since 0.1.8
     */
    const isValid = () => {
        return selectedKind !== undefined && name !== undefined && name.length > 0;
    }

    /**
     * Create new QRCode basic upon local states.
     * @author Kacper Faber
     * @since 0.1.8
     */
    const create = async () => {
        const image = await QRCodeHelper.generateSimpleFile(payload);
        return QRCodeApi.create({...payload, image});
    }

    /**
     * Wrap CreateQRCodeContext context
     * @param children
     * @author Kacper Faber
     * @since 0.1.8
     */
    const wrapContext = (children: ReactNode) => {
        return (
            <CreateQRCodeContext.Provider
                value={{
                    kind: selectedKind,
                    setKind: setSelectedKind,
                    name,
                    setName,
                    isValid,
                    create,
                    step,
                    setStep,
                    payload,
                    setPayload
                }}
                children={children}
            />
        );
    }

    /**
     * Renders appropriate form.
     * @author Kacper Faber
     * @since 0.1.8
     */
    const renderForm = () => {
        return step == 'choose-kind' ? <ChooseKind/> : <InputData/>;
    }

    /**
     * Update kind in payload when is changed in state.
     * @author Kacper Faber
     * @since 0.1.8
     */
    const synchronizeKind = () => {
        if (selectedKind != null) {
            setPayload({...payload, kind: selectedKind}); // TODO: Verify this, it's a workaround with some suspicious
        }
    }

    useEffect(synchronizeKind, [selectedKind]);

    return wrapContext(
        <div className={classNames.root}>
            {renderForm()}

            {isValid() ? <Button children="Utwórz" onClick={create}/> : null}

            <Overlay/>
        </div>
    );
}

export default CreateQRCodeView;

// TODO: CreateQRCodeView.tsx
//  Implement the page.
//  Add floating sidebar with buttons.
//  Add segmented progress bar